window.BUTVariation = window.BUTVariation || class BUTVariation extends BUTCustomComponent {
  constructor() {
    super();
    this.productVariations = this.$$('.js-variation-item');
    this.sectionId = this.dataset.sectionId;
    this.mode = this.dataset.mode || 'product';
    this.cachedData = {};
    this.cachedNavData = {};
    this.cachedLinkedSectionData = {};
    this.cardImageCache = {};
    this.swiperInstance = null;
    this.bindEventHandlers();
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();
    this.initSwiper();
  }

  disconnectedCallback() {
    if (this.swiperInstance) {
      try {
        this.swiperInstance.destroy(true, true);
      } catch (e) {
        console.error("Error destroying Swiper:", e);
      }
      this.swiperInstance = null;
    }
    super.disconnectedCallback && super.disconnectedCallback();
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') {
      // This file is deferred from the page body, ahead of swiper.min.js, so
      // Swiper is usually not here yet. Wait for its onload event (or window
      // load) rather than a 100ms poll per card that never stops if the script
      // fails.
      const retry = () => {
        if (typeof Swiper === 'undefined') return;
        document.removeEventListener('but:swiper:ready', retry);
        window.removeEventListener('load', retry);
        this.initSwiper();
      };
      document.addEventListener('but:swiper:ready', retry);
      window.addEventListener('load', retry);
      return;
    }

    const swiperContainer = this.$('.swiper');
    if (!swiperContainer) return;

    // Destroy existing instance if any
    if (this.swiperInstance) {
      try {
        this.swiperInstance.destroy(true, true);
      } catch (e) {
        console.error("Error destroying existing Swiper:", e);
      }
    }

    // Update productVariations reference after DOM structure change
    this.productVariations = this.$$('.js-variation-item');

    // Get navigation buttons
    const prevBtn = this.$('.js-variation-swiper-prev-btn');
    const nextBtn = this.$('.js-variation-swiper-next-btn');

    // Initialize Swiper
    this.swiperInstance = new Swiper(swiperContainer, {
      slidesPerView: 4,
      spaceBetween: 4,
      grabCursor: true,
      watchOverflow: true,
      navigation: {
        nextEl: nextBtn || undefined,
        prevEl: prevBtn || undefined,
        disabledClass: 'is-disabled',
        hiddenClass: 'is-hidden',
      },
      breakpoints: {
        320: {
          slidesPerView: 4,
          spaceBetween: 4,
          slidesOffsetAfter: 24,
        },
        769: {
          slidesPerView: 5,
          spaceBetween: 0,
          slidesOffsetAfter: 0
        },
      },
      on: {
        init: () => this.updateNavigationButtons(),
        slideChange: () => this.updateNavigationButtons(),
      },
    });
  }

  updateNavigationButtons() {
    if (!this.swiperInstance) return;
    const prevBtn = this.$('.js-variation-swiper-prev-btn');
    const nextBtn = this.$('.js-variation-swiper-next-btn');

    if (prevBtn) {
      if (this.swiperInstance.isBeginning) {
        prevBtn.classList.add('is-disabled');
      } else {
        prevBtn.classList.remove('is-disabled');
      }
    }

    if (nextBtn) {
      if (this.swiperInstance.isEnd) {
        nextBtn.classList.add('is-disabled');
      } else {
        nextBtn.classList.remove('is-disabled');
      }
    }
  }

  bindEventHandlers() {
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    const item = event.target.closest('.js-variation-item');
    if (item) {
      if (this.mode === 'card') {
        this.handleCardVariationClick(item.dataset.variationUrl, item);
      } else {
        this.handleVariationClick(item.dataset.variationUrl, item);
      }
    }
  }

  // Regions that actually differ between linked variation products (i.e. that
  // read from `product`/`product.selected_or_first_available_variant` in an
  // always-rendered wrapper). Blocks whose wrapper is only rendered
  // conditionally (product_type, attributes, pickup_availability, ...) are
  // deliberately left out: swapping by index
  // breaks as soon as a block appears/disappears between the two products, so
  // those are safer left stale than half-patched. `product__block-description`
  // is safe to include because product-description.liquid always renders its
  // wrapper (with a `hide-empty` class hiding it via CSS when blank), so its
  // position never shifts between products. Everything else in the
  // section renders identical markup for the same section id regardless of
  // product, so swapping only the regions below leaves the swiper, the scroll
  // position and the layout untouched.
  //
  // Order matters: replacing a node re-upgrades the custom elements inside it,
  // and they cache references to their neighbours on connectedCallback. So a
  // region has to be swapped before anything that caches it -- but-prd-frm
  // caches .js-product-quantity, and but-variant-radios/-selects caches the
  // gallery, price, sku, inventory, quantity and form. The picker is therefore
  // always last, or it would come back holding detached nodes and break
  // add-to-cart.
  get regionSelectors() {
    return [
      '.product__info-breadcrumb-mobile',
      '.product__info-breadcrumb',
      '.product__title',
      '.js-product-variation-title',
      '.js-product-price',
      '.js-product-discount-price',
      '.js-product-sku',
      '.product__block-vendor',
      '.product__block-description',
      '.product__block-share',
      '.product__block-complementary_products',
      '.key-features-product',
      '.js-product-slideshow-container',
      '.js-inventory-tracking',
      '.js-product-quantity-container',
      // Rendered after the section container closes, so these are matched
      // against the shopify-section wrapper rather than the section itself.
      'but-product-recently-viewed',
      'script[type="application/ld+json"]',
      '.js-product-sticky-atc',
      '.js-but-product-form',
      '.js-variant-picker',
    ];
  }

  handleVariationClick(url, item) {
    if (!url) return;
    // Paint the selection now. The strip is never rebuilt, so it never has to
    // snap back to slide 0 while waiting on the network.
    if (item) {
      this.$$('.js-variation-item').forEach((el) => el.classList.remove('is_active'));
      item.classList.add('is_active');
    }
    this.fetchData(url);
  }

  async handleCardVariationClick(url, item) {
    if (!url) return;

    this.querySelectorAll('.js-variation-item').forEach(item => {
      item.classList.remove('is_active');
    });
    item.classList.add('is_active');

    const imageItemContainer = this.closest('.product-card')?.querySelector('.product__image-first');
    const imageItem = imageItemContainer?.querySelector('but-image-atf');
    if (!imageItem) return;

    // Guard against out-of-order responses when several swatches are clicked quickly.
    const requestId = (this.cardRequestId || 0) + 1;
    this.cardRequestId = requestId;

    imageItem.classList.add('is-loading');
    try {
      let imageMarkup = this.cardImageCache[url];

      if (imageMarkup === undefined) {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch product: ${response.status} ${response.statusText}`);
        }

        const html = await response.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const sourceEl = tempDiv.querySelector('.js-main-product');
        const imageItemSource = sourceEl?.querySelector('but-image-atf');
        imageMarkup = imageItemSource ? imageItemSource.innerHTML : null;
        this.cardImageCache[url] = imageMarkup;
      }

      if (imageMarkup && this.cardRequestId === requestId) {
        imageItem.innerHTML = imageMarkup;
      }
    } catch (error) {
      console.error('Error fetching product variation:', error);
    } finally {
      if (this.cardRequestId === requestId) {
        imageItem.classList.remove('is-loading');
      }
    }
  }

  async fetchData(productUrl) {
    // Switching back and forth faster than the network answers used to let an
    // earlier response land on top of a later one, leaving the page on one
    // product and the tab strip on another.
    const requestId = (this.requestId || 0) + 1;
    this.requestId = requestId;

    try {
      let dom = this.cachedData[productUrl];

      if (!dom) {
        const fetchUrl = `${productUrl}?section_id=${this.sectionId}`;
        const response = await fetch(fetchUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch product: ${response.status} ${response.statusText}`);
        }

        const html = await response.text();
        dom = document.createElement('div');
        dom.innerHTML = html;

        this.cachedData[productUrl] = dom;

        await this.preloadImages(dom);
      }

      if (this.requestId !== requestId) return;

      // No scroll restoration needed: updateDOM swaps regions in place instead
      // of rebuilding the section, so the viewport never moves.
      this.updateDOM(dom, productUrl);

      // The navigation is its own section and has to follow the product on every
      // switch. Leaving it behind the cache check meant a return to an already
      // visited variation kept the previous product's tabs and blocks.
      await Promise.all([
        this.updateNavigation(productUrl, requestId),
        this.updateLinkedSections(productUrl, requestId),
      ]);
    } catch (error) {
      console.error('Error fetching product variation:', error);
    }
  }

  async updateNavigation(productUrl, requestId) {
    const navSection = document.querySelector('.js-product-navigation');
    if (!navSection) return;

    // Read the section id off the page instead of hard-coding it: a hard-coded id
    // stops resolving as soon as the section is re-added or the template changes,
    // and the nav then silently keeps the previous product's content.
    const navSectionId = navSection
      .closest('[id^="shopify-section-"]')
      ?.id.replace('shopify-section-', '');
    if (!navSectionId) return;

    let navDom = this.cachedNavData[productUrl];

    if (!navDom) {
      const response = await fetch(`${productUrl}?section_id=${navSectionId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch navigation: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      navDom = document.createElement('div');
      navDom.innerHTML = html;

      this.cachedNavData[productUrl] = navDom;
    }

    if (requestId !== undefined && this.requestId !== requestId) return;

    const source = navDom.querySelector('.js-product-navigation');
    if (!source) return;

    navSection.innerHTML = source.innerHTML;
  }

  // Standalone sections that render from `product` (e.g. product-before-after)
  // are not part of the main product section, so they would keep showing the
  // first product's content. Each one renders an always-present
  // `.js-*` wrapper hook, so re-render it for the new product.
  get linkedSectionSelectors() {
    return ['.js-product-before-after'];
  }

  async updateLinkedSections(productUrl, requestId) {
    const targets = this.linkedSectionSelectors.flatMap((selector) =>
      Array.from(document.querySelectorAll(selector)).map((el) => ({ el, selector }))
    );

    await Promise.all(
      targets.map(async ({ el, selector }) => {
        const sectionId = el
          .closest('[id^="shopify-section-"]')
          ?.id.replace('shopify-section-', '');
        if (!sectionId) return;

        const cacheKey = `${productUrl}::${sectionId}`;
        let html = this.cachedLinkedSectionData[cacheKey];

        if (html === undefined) {
          try {
            const response = await fetch(`${productUrl}?section_id=${sectionId}`);
            if (!response.ok) {
              throw new Error(`Failed to fetch section ${sectionId}: ${response.status} ${response.statusText}`);
            }
            const dom = document.createElement('div');
            dom.innerHTML = await response.text();
            html = dom.querySelector(selector)?.innerHTML ?? null;
            this.cachedLinkedSectionData[cacheKey] = html;
          } catch (error) {
            console.error('Error fetching linked section:', error);
            return;
          }
        }

        if (html === null || this.requestId !== requestId) return;

        // innerHTML re-creates the custom elements inside (before-after-slider),
        // so they reconnect against the new markup.
        el.innerHTML = html;
      })
    );
  }

  preloadImages(dom) {
    // Lazy images inside a detached document never start loading, so the old
    // `:not([loading="lazy"])` filter resolved instantly and the gallery popped
    // in after the swap, shifting the layout a second time. Mirror the first few
    // gallery frames into eager Image() objects and wait on those instead.
    const sources = Array.from(
      dom.querySelectorAll('.js-product-slideshow img[src]')
    ).slice(0, 6);

    if (sources.length === 0) {
      return Promise.resolve();
    }

    return Promise.all(
      sources.map((img) => {
        return new Promise((resolve) => {
          const preload = new Image();
          preload.onload = () => resolve();
          preload.onerror = () => resolve();
          if (img.srcset) preload.srcset = img.srcset;
          if (img.sizes) preload.sizes = img.sizes;
          preload.src = img.src;
          if (preload.complete) resolve();
        });
      })
    );
  }

  updateDOM(responseHTML, productUrl) {
    const targetSectionId = this.sectionId;
    const currentSection = document.querySelector(`#${targetSectionId}`);
    const newSection =
      responseHTML?.querySelector(`#${targetSectionId}`) ||
      responseHTML?.querySelector('.js-main-product');

    if (!currentSection || !newSection) {
      console.warn('Product elements not found in current or new product');
      return;
    }

    // Match against the shopify-section wrapper, not the section container: the
    // recently-viewed tracker and the JSON-LD snippet are rendered after it closes
    // and still have to follow the product.
    const host = currentSection.parentElement;
    const newHost = newSection.parentElement || newSection;

    // Pin the height across the swap so the document never collapses and the
    // browser never re-clamps the scroll offset.
    host.style.minHeight = `${host.offsetHeight}px`;

    this.regionSelectors.forEach((selector) => {
      const targets = Array.from(host.querySelectorAll(selector));
      const sources = Array.from(newHost.querySelectorAll(selector));
      targets.forEach((target, index) => {
        const source = sources[index];
        if (!source) return;
        // replaceWith re-runs connectedCallback on the custom elements inside;
        // assigning innerHTML on the element itself would not.
        target.replaceWith(source.cloneNode(true));
      });
    });

    // The strip itself is deliberately left alone -- handleVariationClick has
    // already moved `is_active`, so the swiper keeps its instance and its
    // scroll offset instead of being destroyed and restarted at slide 0.
    if (productUrl) {
      window.history.replaceState({}, '', productUrl);
    }

    if (window.Shopify?.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    requestAnimationFrame(() => {
      host.style.minHeight = '';
    });
  }
}
if (!customElements.get('but-variation')) {
  customElements.define('but-variation', window.BUTVariation);
}